import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, addDoc, onSnapshot, query, where, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { logMonitoringEvent } from '../utils/monitoringLogger';

const MAX_RENDER_PER_CONTESTANT = 200; // hard cap to protect DOM
const MAX_GLOBAL_FETCH = 1500; // hard ceiling on docs subscribed at once

/**
 * Hybrid reactions:
 * - Writes always go to top-level `reactions/` collection with `roomScope` field set to current room
 * - Reads:
 *    global mode: subscribe to all reactions ordered by timestamp desc, limit MAX_GLOBAL_FETCH
 *    degraded mode: subscribe filtered by roomScope IN myRoomIds (max 10 IDs in `in` query)
 *
 * The contestant key is the contestant id; reactions w/ contestantId map to a row stack.
 */
export const useReactions = (roomId, opts) => {
  const { mode = 'global', myRoomIds = [] } = opts || {};
  const [reactions, setReactions] = useState([]);
  const lastSentRef = useRef(0);

  useEffect(() => {
    let q;
    try {
      if (mode === 'degraded') {
        const ids = (myRoomIds && myRoomIds.length > 0) ? myRoomIds.slice(0, 10) : (roomId ? [roomId] : []);
        if (ids.length === 0) { setReactions([]); return; }
        q = query(
          collection(db, 'reactions'),
          where('roomScope', 'in', ids),
          orderBy('timestamp', 'desc'),
          limit(MAX_GLOBAL_FETCH)
        );
      } else {
        q = query(
          collection(db, 'reactions'),
          orderBy('timestamp', 'desc'),
          limit(MAX_GLOBAL_FETCH)
        );
      }
    } catch (e) {
      console.error('useReactions query build failed:', e);
      return;
    }

    const unsub = onSnapshot(q, (snap) => {
      // Group by contestant client-side, cap at MAX_RENDER_PER_CONTESTANT (latest by timestamp)
      const byContestant = new Map();
      snap.docs.forEach(d => {
        const data = d.data();
        const cid = String(data.contestantId ?? 'none');
        if (!byContestant.has(cid)) byContestant.set(cid, []);
        byContestant.get(cid).push({ id: d.id, ...data });
      });
      const out = [];
      byContestant.forEach(arr => {
        arr.sort((a, b) => {
          const ta = a.timestamp?.toMillis?.() ?? 0;
          const tb = b.timestamp?.toMillis?.() ?? 0;
          return ta - tb;
        });
        if (arr.length > MAX_RENDER_PER_CONTESTANT) {
          arr.splice(0, arr.length - MAX_RENDER_PER_CONTESTANT);
        }
        out.push(...arr);
      });
      setReactions(out);
    }, (err) => {
      console.error('useReactions onSnapshot error:', err);
    });
    return unsub;
  }, [mode, roomId, JSON.stringify(myRoomIds)]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * sendReaction: single write to top-level `reactions/` with roomScope = current room.
   * In global mode all clients see it. In degraded mode only clients whose myRoomIds includes roomScope see it.
   */
  const sendReaction = useCallback(async (userId, userName, emoji, contestantId) => {
    if (!roomId) return;
    const now = Date.now();
    if (now - lastSentRef.current < 3000) return;
    lastSentRef.current = now;
    try {
      await addDoc(collection(db, 'reactions'), {
        userId,
        userName,
        emoji,
        contestantId,
        roomScope: roomId,
        timestamp: Timestamp.now(),
      });
      if (navigator.vibrate) navigator.vibrate(50);
    } catch (e) {
      console.error('Failed to send reaction:', e);
      logMonitoringEvent('error', 'firestore', `Failed to send reaction: ${e.message}`, {
        userId, userName, metadata: { emoji, contestantId, roomScope: roomId, mode },
      });
    }
  }, [roomId, mode]);

  return { reactions, sendReaction };
};
